/**
 * @component ry-divider
 * @category  layout
 * @summary   Visual separator rule (Fomantic UI `.ui.divider` equivalent). Horizontal by default with an optional inline text label that splits the rule into two segments; set `vertical` for a column-separator variant. Pure display + ARIA — no events, no methods.
 *
 * Reference: Fomantic UI's divider element. ry-divider covers the common
 * cases: simple horizontal rule, labeled "OR" / "AND" divider with text in
 * the middle, and vertical column separator. Fomantic's "fitted" / "hidden" /
 * "section" / "clearing" variants are intentionally NOT modelled — use spacing
 * utilities and `<hr>` if you need the bare-rule behavior.
 *
 * @attribute text       string?              Optional label rendered in the middle of a horizontal divider. When set, the rule splits into two halves with the text between them. Has no visual effect on vertical dividers (where text would be sideways).
 * @attribute vertical   boolean   default=false   Renders a vertical column separator instead of a horizontal one. Use inside a flex/grid row to separate two columns.
 *
 * @content   None — `<ry-divider>` is configured entirely via the `text` attribute. innerHTML is replaced at setup when text is present.
 *
 * @dom (horizontal, no text)
 *   ry-divider   role="separator"   aria-orientation="horizontal"
 *
 * @dom (horizontal, with text)
 *   ry-divider[text]   role="separator"   aria-orientation="horizontal"
 *     > span.ry-divider__text   [data-ry-target="text"]   {text}
 *
 * @dom (vertical)
 *   ry-divider[vertical]   role="separator"   aria-orientation="vertical"
 *
 * @gotcha  ARIA orientation is set automatically based on `[vertical]`. Consumers should NOT also set `aria-orientation` — the component owns it.
 *
 * @gotcha  Text dividers are horizontal-only by design. Setting `text` on a `[vertical]` divider renders the slot but it visually looks broken (text would rotate sideways). Mixing the two is an authoring error — no warning, no enforcement.
 *
 * @gotcha  Live update of `text` is supported — the slot is added, replaced, or removed in place. Live update of `vertical` is also supported (CSS reads the attribute and ARIA is re-applied).
 *
 * @gotcha  ry-divider does NOT support Fomantic's `hidden` variant (no spacing). Use plain spacing utilities or remove the element instead.
 *
 * @example basic
 * ```html
 * <p>Above</p>
 * <ry-divider></ry-divider>
 * <p>Below</p>
 * ```
 *
 * @example with-text
 * ```html
 * <ry-button>Sign in with Google</ry-button>
 * <ry-divider text="OR"></ry-divider>
 * <ry-button>Sign in with Email</ry-button>
 * ```
 *
 * @example vertical
 * ```html
 * <div style="display:flex; gap:1rem; align-items:center; height:3rem">
 *   <span>Left column</span>
 *   <ry-divider vertical></ry-divider>
 *   <span>Right column</span>
 * </div>
 * ```
 *
 * @tutorial mount-sets-role-and-orientation
 * ```js
 * const el = mount('<ry-divider></ry-divider>');
 * await expectAttr(el, 'role', 'separator', 'role="separator" applied');
 * await expectAttr(el, 'aria-orientation', 'horizontal', 'horizontal by default');
 * ```
 *
 * @tutorial vertical-flips-aria-orientation
 * ```js
 * const el = mount('<ry-divider vertical></ry-divider>');
 * await expectAttr(el, 'aria-orientation', 'vertical', 'vertical orientation reflected');
 * ```
 *
 * @tutorial text-attribute-renders-slot
 * ```js
 * const el = mount('<ry-divider text="OR"></ry-divider>');
 * const slot = el.querySelector('[data-ry-target="text"]');
 * await expectEqual(!!slot, true, 'text slot rendered');
 * await expectEqual(slot.textContent, 'OR');
 * ```
 *
 * @tutorial no-text-slot-without-attribute
 * ```js
 * // Wrong assumption: the slot always renders, possibly empty. It does NOT —
 * // a plain `<ry-divider>` is a pure rule with no inner element.
 * const el = mount('<ry-divider></ry-divider>');
 * await expectEqual(el.querySelector('[data-ry-target="text"]'), null, 'no text slot without attribute');
 * ```
 *
 * @tutorial live-text-update
 * ```js
 * const el = mount('<ry-divider text="OR"></ry-divider>');
 * await expectEqual(el.querySelector('[data-ry-target="text"]').textContent, 'OR');
 * el.setAttribute('text', 'AND');
 * await expectEqual(el.querySelector('[data-ry-target="text"]').textContent, 'AND', 'live text replaced');
 * ```
 *
 * @tutorial adding-and-removing-text-live
 * ```js
 * const el = mount('<ry-divider></ry-divider>');
 * await expectEqual(el.querySelector('[data-ry-target="text"]'), null, 'no slot to start');
 * el.setAttribute('text', 'NEW');
 * await expectEqual(el.querySelector('[data-ry-target="text"]').textContent, 'NEW', 'slot inserted');
 * el.removeAttribute('text');
 * await expectEqual(el.querySelector('[data-ry-target="text"]'), null, 'slot removed');
 * ```
 *
 * @tutorial vertical-toggle-updates-orientation-live
 * ```js
 * const el = mount('<ry-divider></ry-divider>');
 * await expectAttr(el, 'aria-orientation', 'horizontal');
 * el.setAttribute('vertical', '');
 * await expectAttr(el, 'aria-orientation', 'vertical', 'orientation updates live');
 * el.removeAttribute('vertical');
 * await expectAttr(el, 'aria-orientation', 'horizontal');
 * ```
 *
 * @see ry-card
 * @see ry-loader
 */

import { RyElement } from '../core/ry-element.js';

export class RyDivider extends RyElement {
  static get observedAttributes(): string[] {
    return ['text', 'vertical'];
  }

  setup(): void {
    this.#applyAria();
    this.#renderText();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;

    if (name === 'text') {
      this.#renderText();
    } else if (name === 'vertical') {
      this.#applyAria();
    }
  }

  #applyAria(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'separator');
    this.setAttribute('aria-orientation', this.hasAttribute('vertical') ? 'vertical' : 'horizontal');
  }

  #renderText(): void {
    const text = this.getAttribute('text');
    let slot = this.$<HTMLElement>('[data-ry-target="text"]');
    if (text) {
      if (!slot) {
        slot = document.createElement('span');
        slot.setAttribute('data-ry-target', 'text');
        slot.className = 'ry-divider__text';
        this.appendChild(slot);
      }
      slot.textContent = text;
    } else if (slot) {
      slot.remove();
    }
  }
}

customElements.define('ry-divider', RyDivider);
